import { createStyles, makeStyles, Toolbar } from "@material-ui/core";
import {
    Button,
    HelpIcon,
    MarketResearchConfigurationSection,
    MarketResearchTool,
    MarketResearchType,
    Page,
    PageActionButton,
    PageHeaderActions,
    PageSection,
    RelodeTheme,
    ShareAltIcon,
    Text,
} from "@relode/core";
import * as React from "react";

import { useClientMarketResearchContext } from "./_infra/clientMarketResearchProvider";
import { ClientMarketResearchProps } from "./clientMarketResearchProps";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                display: "flex",
                flexDirection: "row",
                flexGrow: 1,
                padding: theme.spacing(0, 2),
            },

            criteria: {
                display: "flex",
                flexDirection: "column",
                flexGrow: 0,
                width: 250,
                marginRight: theme.spacing(4),
            },
            results: {
                flexGrow: 1,
            },
            configureHelperContainer: {
                minHeight: 230,
                width: "100%",
                backgroundColor: theme.palette.grey[100],
                marginBottom: theme.spacing(2),
                borderRadius: 6,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
            },
            configureHelperText: {
                color: theme.palette.grey[700],
            },
        }),
    { name: "ClientMarketResearchDesktop" }
);

export const ClientMarketResearchDesktop = (
    props: ClientMarketResearchProps
) => {
    const classes = useStyles({});
    const {
        onHelp,
        onShare,
        onSearch,
        onJobTitleChange,
        onLocationChange,
        searchCriteria: searchCriteriaProp,
        helpSection,
        recentSearches = [],
    } = props;
    const [state] = useClientMarketResearchContext();

    const {
        searchCriteria,
        searchResults,
        searchResults: {
            summary: { data: summaryData },
        },
        searchMode,
    } = state;

    // Disable search button if these haven't changed
    const searchDisabled =
        (searchCriteriaProp?.jobName === searchCriteria?.jobName &&
            searchCriteriaProp?.locationId === searchCriteria?.locationId) ||
        !searchCriteriaProp?.jobName?.trim() ||
        !searchCriteriaProp?.locationId;

    const renderActionBar = (): React.ReactNode => (
        <PageHeaderActions
            right={
                !!summaryData ? (
                    <>
                        <PageActionButton
                            id="marketResearchHelpButton"
                            onClick={onHelp}
                            color="primary"
                            title="help with market research"
                        >
                            <HelpIcon />
                        </PageActionButton>
                        <PageActionButton
                            id="marketResearchShareButton"
                            onClick={onShare}
                            color="primary"
                            title="share market research"
                        >
                            <ShareAltIcon />
                        </PageActionButton>
                    </>
                ) : null
            }
        />
    );

    const renderMarketResearchResults = () => {
        //default when there's no config - coming into a fresh instance of marketResearch
        if (searchMode === "configure") {
            return (
                <div className={classes.configureHelperContainer}>
                    <Text className={classes.configureHelperText}>
                        use the configure section to the left to get your market
                        research
                    </Text>
                </div>
            );
        }

        //marketResearch upon successful configuration/normalization
        return (
            <MarketResearchTool
                state={searchResults}
                marketResearchType={MarketResearchType.PrivateTool}
                helpSection={helpSection}
            />
        );
    };

    return (
        <Page
            id="clientMarketResearchConfiguration"
            label="market research"
            actions={renderActionBar()}
            busyType="bar"
        >
            <PageSection>
                <div className={classes.root}>
                    <div className={classes.criteria}>
                        <MarketResearchConfigurationSection
                            searchCriteria={searchCriteriaProp}
                            onJobTitleChange={onJobTitleChange}
                            onLocationFilter={onLocationChange}
                            recentSearches={recentSearches}
                            searchAction={
                                <Button
                                    data-cy="clientMarketResearchConfigurationButton"
                                    fullWidth
                                    id="clientMarketResearchConfigurationButton"
                                    onClick={() => onSearch(searchCriteriaProp)}
                                    size="small"
                                    color="primary"
                                    variant="contained"
                                    disabled={searchDisabled}
                                >
                                    search
                                </Button>
                            }
                        />
                    </div>
                    <div className={classes.results}>
                        <Toolbar disableGutters={true}>
                            <Text variant="h2">results</Text>
                        </Toolbar>
                        {renderMarketResearchResults()}
                    </div>
                </div>
            </PageSection>
        </Page>
    );
};
